import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Mail, 
  Save, 
  RotateCcw, 
  Info, 
  CheckCircle, 
  AlertTriangle, 
  ChevronDown,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  FileText,
  Clock,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView, Decoration, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';
import type { DecorationSet } from '@codemirror/view';

// Template type definition (matches backend)
type TemplateType = "passwordReset" | "welcome" | "notification" | "reminder" | "confirmation";

interface TemplateTypeInfo {
  type: TemplateType;
  name: string;
  description: string;
  hasTemplate: boolean;
  isEnabled?: boolean;
  lastUpdated?: number;
}

interface Template {
  _id: string;
  _creationTime: number;
  templateType: TemplateType;
  subject: string;
  htmlContent: string;
  description?: string;
  isEnabled?: boolean;
  updatedBy: string;
  updatedAt: number;
}

export function EmailTemplateManager() {
  const [currentView, setCurrentView] = useState<'list' | 'edit'>('list');
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Template editing state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationResults, setValidationResults] = useState<{
    warnings: { type: 'warning' | 'error' | 'info'; message: string; line?: number }[];
    score: number;
    isValidating: boolean;
  }>({ warnings: [], score: 100, isValidating: false });

  // Use backend functions to get template data
  const templateTypesData = useQuery(api.emailTemplates.getTemplateTypes);
  const currentTemplate = useQuery(api.emailTemplates.getEmailTemplate, 
    selectedTemplateType ? { templateType: selectedTemplateType } : "skip"
  );
  const siteConfig = useQuery(api.siteConfig.getSiteConfig);

  // Mutations
  const upsertTemplate = useMutation(api.emailTemplates.upsertEmailTemplate);
  const createNewTemplate = useMutation(api.emailTemplates.createNewTemplate);
  const deleteTemplate = useMutation(api.emailTemplates.deleteEmailTemplate);

  // Use actual backend data
  const templateTypes: TemplateTypeInfo[] = templateTypesData || [];

  // Email validation and formatting function
  const validateAndFormatEmail = () => {
    setValidationResults(prev => ({ ...prev, isValidating: true }));
    
    const warnings: { type: 'warning' | 'error' | 'info'; message: string; line?: number }[] = [];
    let score = 100;
    
    // Basic HTML validation
    if (!htmlContent.trim()) {
      warnings.push({ type: 'error', message: 'Email template cannot be empty' });
      score -= 50;
    }
    
    // Check for required email structure
    if (!htmlContent.includes('<!DOCTYPE') && !htmlContent.includes('<html')) {
      warnings.push({ 
        type: 'warning', 
        message: 'Consider adding DOCTYPE html and <html> tags for better email client compatibility' 
      });
      score -= 10;
    }
    
    // Check for inline CSS (required for emails)
    const hasExternalCSS = htmlContent.includes('<link') || htmlContent.includes('<style>');
    if (hasExternalCSS) {
      warnings.push({ 
        type: 'error', 
        message: 'External CSS and <style> tags are not reliable in emails. Use inline styles only.' 
      });
      score -= 20;
    }
    
    // Check for table-based layout
    if (!htmlContent.includes('<table') && htmlContent.length > 200) {
      warnings.push({ 
        type: 'info', 
        message: 'Consider using table-based layout for better email client support' 
      });
      score -= 5;
    }
    
    // Check alt text for images
    const imgMatches = htmlContent.match(/<img[^>]*>/g) || [];
    imgMatches.forEach((img, index) => {
      if (!img.includes('alt=')) {
        warnings.push({ 
          type: 'warning', 
          message: `Image ${index + 1} missing alt text for accessibility and spam prevention` 
        });
        score -= 5;
      }
    });
    
    // Check for spam trigger words
    const spamWords = ['FREE', 'URGENT', 'ACT NOW', 'CLICK HERE', 'GUARANTEE', 'WINNER'];
    const contentText = htmlContent.replace(/<[^>]*>/g, '').toUpperCase();
    spamWords.forEach(word => {
      if (contentText.includes(word)) {
        warnings.push({ 
          type: 'warning', 
          message: `Avoid spam trigger word: "${word}" - use alternatives` 
        });
        score -= 3;
      }
    });
    
    // Check subject line
    if (subject.length > 50) {
      warnings.push({ 
        type: 'warning', 
        message: `Subject line is ${subject.length} characters. Consider keeping under 50 for mobile.` 
      });
      score -= 5;
    }
    
    // Check for responsive design
    if (!htmlContent.includes('max-width') && !htmlContent.includes('width: 100%')) {
      warnings.push({ 
        type: 'info', 
        message: 'Consider adding responsive design with max-width and fluid widths' 
      });
      score -= 5;
    }
    
    // Check template variables usage
    const hasPersonalization = htmlContent.includes('{{') && htmlContent.includes('}}');
    if (hasPersonalization) {
      warnings.push({ 
        type: 'info', 
        message: 'Great! Using template variables improves engagement and deliverability' 
      });
    } else {
      warnings.push({ 
        type: 'info', 
        message: 'Consider adding personalization with template variables ({{appName}}, {{companyName}})' 
      });
      score -= 3;
    }
    
    // Email client specific checks
    if (htmlContent.includes('position: fixed') || htmlContent.includes('position: absolute')) {
      warnings.push({ 
        type: 'error', 
        message: 'Avoid fixed/absolute positioning - not supported in many email clients' 
      });
      score -= 15;
    }
    
    if (htmlContent.includes('flexbox') || htmlContent.includes('display: flex')) {
      warnings.push({ 
        type: 'warning', 
        message: 'Flexbox has limited email client support - test thoroughly' 
      });
      score -= 10;
    }
    
    // Security checks
    if (htmlContent.includes('javascript:') || htmlContent.includes('<script')) {
      warnings.push({ 
        type: 'error', 
        message: 'JavaScript not allowed in emails - will be stripped by email clients' 
      });
      score -= 25;
    }
    
    // Accessibility checks
    if (!htmlContent.includes('role=') && htmlContent.includes('<table')) {
      warnings.push({ 
        type: 'info', 
        message: 'Consider adding ARIA roles for better accessibility (role="presentation" for layout tables)' 
      });
      score -= 2;
    }
    
    setTimeout(() => {
      setValidationResults({ 
        warnings, 
        score: Math.max(0, score), 
        isValidating: false 
      });
    }, 1000); // Simulate processing time
  };

  // Custom theme to style template variables and user-facing text
  const emailContentTheme = EditorView.theme({
    '&': {
      whiteSpace: 'pre-wrap !important',
    },
    // Template variables (blue)
    '.cm-template-variable': {
      fontWeight: 'bold',
      backgroundColor: 'rgb(239 246 255)', // blue-50
      color: 'rgb(30 64 175)', // blue-800
      padding: '1px 3px',
      borderRadius: '3px',
      border: '1px solid rgb(147 197 253)', // blue-300
    },
    '.cm-color-variable': {
      position: 'relative',
      paddingLeft: '20px',
    },
    '.cm-color-variable::before': {
      content: '" "', // Non-breaking space
      position: 'absolute',
      left: '2px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '12px',
      height: '12px',
      borderRadius: '3px',
      border: '1px solid rgb(0 0 0 / 0.2)',
      boxShadow: '0 1px 2px rgb(0 0 0 / 0.1), inset 0 0 0 1px rgb(255 255 255 / 0.1)',
    },
    // Color-specific variables
    '.cm-primary-color::before': { backgroundColor: 'var(--primary-color)' },
    '.cm-secondary-color::before': { backgroundColor: 'var(--secondary-color)' },
    '.cm-accent-color::before': { backgroundColor: 'var(--accent-color)' },
    '.cm-success-color::before': { backgroundColor: 'var(--success-color)' },
    '.cm-error-color::before': { backgroundColor: 'var(--error-color)' },
    '.cm-background-color::before': { backgroundColor: 'var(--background-color)' },
    '.cm-text-color::before': { backgroundColor: 'var(--text-color)' },
    '.cm-muted-text-color::before': { backgroundColor: 'var(--muted-text-color)' },
    '.dark .cm-template-variable': {
      backgroundColor: 'rgb(30 58 138)', // blue-900
      color: 'rgb(147 197 253)', // blue-300
      border: '1px solid rgb(59 130 246)', // blue-500
    },
    // User-facing text content (green)
    '.cm-user-text': {
      fontWeight: '600',
      backgroundColor: 'rgb(240 253 244)', // green-50
      color: 'rgb(22 101 52)', // green-800
      padding: '1px 2px',
      borderRadius: '3px',
    },
    '.dark .cm-user-text': {
      backgroundColor: 'rgb(20 83 45)', // green-900
      color: 'rgb(134 239 172)', // green-300
    },
    // Color preview boxes for hex values
    '.cm-color-preview': {
      position: 'relative',
      fontWeight: '500',
    },
    '.cm-color-preview::before': {
      content: '""',
      display: 'inline-block',
      width: '12px',
      height: '12px',
      backgroundColor: 'var(--preview-color)',
      border: '1px solid rgb(0 0 0 / 0.1)',
      borderRadius: '2px',
      marginRight: '4px',
      verticalAlign: 'middle',
      boxShadow: '0 0 0 1px rgb(255 255 255 / 0.3)',
    },
    '.dark .cm-color-preview::before': {
      border: '1px solid rgb(255 255 255 / 0.2)',
    },
  });

  // Function to get color preview class for a variable
  const getColorClass = (variable: string) => {
    switch (variable) {
      case 'primaryColor': return 'cm-primary-color';
      case 'secondaryColor': return 'cm-secondary-color';
      case 'accentColor': return 'cm-accent-color';
      case 'successColor': return 'cm-success-color';
      case 'errorColor': return 'cm-error-color';
      case 'backgroundColor': return 'cm-background-color';
      case 'textColor': return 'cm-text-color';
      case 'mutedTextColor': return 'cm-muted-text-color';
      default: return '';
    }
  };

  // Function to create color preview decoration
  const createColorMark = (hexColor: string) => Decoration.mark({
    class: 'cm-color-preview',
    attributes: {
      'data-color': hexColor,
      style: `--preview-color: ${hexColor}`,
    },
  });

  // Simplified plugin to highlight template variables
  const emailContentPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view);
        }
      }

      buildDecorations(view: EditorView): DecorationSet {
        const builder = new RangeSetBuilder<Decoration>();
        const text = view.state.doc.toString();

        try {
          // Collect all decorations first, then sort them
          const decorations: { from: number; to: number; decoration: Decoration }[] = [];

          // Find template variables like {{variableName}}
          const variableRegex = /\{\{([^}]+)\}\}/g;
          let variableMatch;
          
          while ((variableMatch = variableRegex.exec(text)) !== null) {
            const variableName = variableMatch[1];
            const colorClass = getColorClass(variableName);
            const classes = ['cm-template-variable'];
            if (colorClass) {
              classes.push('cm-color-variable', colorClass);
            }
            
            decorations.push({
              from: variableMatch.index,
              to: variableMatch.index + variableMatch[0].length,
              decoration: Decoration.mark({
                class: classes.join(' ')
              })
            });
          }

          // Find hex color values like #ffffff, #000, etc.
          const hexColorRegex = /#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/g;
          let colorMatch;

          while ((colorMatch = hexColorRegex.exec(text)) !== null) {
            const hexValue = colorMatch[0];
            decorations.push({
              from: colorMatch.index,
              to: colorMatch.index + colorMatch[0].length,
              decoration: createColorMark(hexValue)
            });
          }

          // Sort decorations by position (required by CodeMirror)
          decorations.sort((a, b) => a.from - b.from);

          // Add all decorations in sorted order
          decorations.forEach(({ from, to, decoration }) => {
            builder.add(from, to, decoration);
          });

          return builder.finish();
        } catch (error) {
          console.error('Error building decorations:', error);
          return builder.finish();
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  );

  // Helper function to replace template variables with actual values
  const replaceTemplateVariables = (text: string) => {
    // Default values if no siteConfig
    const defaultConfig = {
      appName: 'CFI Dashboard',
      companyName: 'DAFCG',
      companyEmail: 'info@example.com',
      supportEmail: 'support@makably.com',
      websiteUrl: 'http://makably.com',
      emailFooter: '© 2025 DAFCG. All rights reserved.',
      primaryColor: '#0066cc',
      secondaryColor: '#666666',
      accentColor: '#ff9900',
      successColor: '#28a745',
      errorColor: '#dc3545',
      backgroundColor: '#f8f9fa',
      textColor: '#212529',
      mutedTextColor: '#6c757d'
    };

    const config = siteConfig || defaultConfig;
    
    const result = text
      .replace(/{{resetUrl}}/g, 'https://example.com/reset?token=SAMPLE_TOKEN')
      .replace(/{{appName}}/g, config.appName)
      .replace(/{{companyName}}/g, config.companyName)
      .replace(/{{companyEmail}}/g, config.companyEmail)
      .replace(/{{supportEmail}}/g, config.supportEmail)
      .replace(/{{websiteUrl}}/g, config.websiteUrl)
      .replace(/{{emailFooter}}/g, config.emailFooter)
      .replace(/{{userEmail}}/g, 'user@example.com')
      .replace(/{{email}}/g, 'user@example.com')
      .replace(/{{token}}/g, 'sample-token-123')
      // Brand colors
      .replace(/{{primaryColor}}/g, config.primaryColor)
      .replace(/{{secondaryColor}}/g, config.secondaryColor)
      .replace(/{{accentColor}}/g, config.accentColor)
      .replace(/{{successColor}}/g, config.successColor)
      .replace(/{{errorColor}}/g, config.errorColor)
      .replace(/{{backgroundColor}}/g, config.backgroundColor)
      .replace(/{{textColor}}/g, config.textColor)
      .replace(/{{mutedTextColor}}/g, config.mutedTextColor);
    
    return result;
  };

  // Load template data when template changes
  useEffect(() => {
    if (currentTemplate) {
      setSubject(currentTemplate.subject);
      setHtmlContent(currentTemplate.htmlContent);
      setDescription(currentTemplate.description || '');
      setIsEnabled(currentTemplate.isEnabled ?? true);
      setIsModified(false);
      setIsCreating(false);
    } else if (currentTemplate === null && selectedTemplateType && currentView === 'edit') {
      // Creating new template - use default values
      setSubject('');
      setHtmlContent('');
      setDescription('');
      setIsEnabled(true);
      setIsModified(false);
      setIsCreating(true);
    }
  }, [currentTemplate, selectedTemplateType, currentView]);

  const handleFieldChange = (field: 'subject' | 'htmlContent' | 'description' | 'isEnabled', value: string | boolean) => {
    switch (field) {
      case 'subject':
        setSubject(value as string);
        break;
      case 'htmlContent':
        setHtmlContent(value as string);
        break;
      case 'description':
        setDescription(value as string);
        break;
      case 'isEnabled':
        setIsEnabled(value as boolean);
        break;
    }

    if (currentTemplate && !isCreating) {
      const hasChanges = 
        (field === 'subject' ? value : subject) !== currentTemplate.subject ||
        (field === 'htmlContent' ? value : htmlContent) !== currentTemplate.htmlContent ||
        (field === 'description' ? value : description) !== (currentTemplate.description || '') ||
        (field === 'isEnabled' ? value : isEnabled) !== (currentTemplate.isEnabled ?? true);
      setIsModified(hasChanges);
    } else {
      setIsModified(true);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplateType) return;

    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }

    if (!htmlContent.trim()) {
      toast.error("HTML content is required");
      return;
    }

    try {
      setIsSaving(true);
      const result = await upsertTemplate({
        templateType: selectedTemplateType,
        subject: subject.trim(),
        htmlContent: htmlContent.trim(),
        description: description.trim() || undefined,
        isEnabled,
      });

      if (result.success) {
        toast.success(result.message);
        setIsModified(false);
        setIsCreating(false);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save template';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (currentTemplate) {
      setSubject(currentTemplate.subject);
      setHtmlContent(currentTemplate.htmlContent);
      setDescription(currentTemplate.description || '');
      setIsEnabled(currentTemplate.isEnabled ?? true);
    } else {
      setSubject('');
      setHtmlContent('');
      setDescription('');
      setIsEnabled(true);
    }
    setIsModified(false);
  };

  const handleCreateNew = async (templateType: TemplateType) => {
    try {
      const result = await createNewTemplate({ templateType });
      if (result.success) {
        toast.success(result.message);
        // Switch to edit mode for the new template
        setSelectedTemplateType(templateType);
        setCurrentView('edit');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create template';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!currentTemplate) {
      toast.error('No template to delete');
      return;
    }

    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      try {
        const result = await deleteTemplate({ templateId: currentTemplate._id });
        if (result.success) {
          toast.success(result.message);
          // Go back to list view
          setCurrentView('list');
          setSelectedTemplateType(null);
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete template';
        toast.error(errorMessage);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  };

  // Render list view
  if (currentView === 'list') {
    if (templateTypesData === undefined) {
      return (
        <div className="flex items-center justify-center h-32" role="status" aria-live="polite" aria-busy="true">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading templates...</p>
          </div>
        </div>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Email Templates</span>
          </CardTitle>
          <p className="text-sm text-gray-600">
            Manage email templates for different system notifications
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {templateTypes.map((templateType) => (
              <div key={templateType.type} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-500" />
                    <div>
                      <h3 className="font-medium text-gray-900">{templateType.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{templateType.description}</p>
                      {templateType.lastUpdated && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          Last updated: {formatDate(templateType.lastUpdated)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      {templateType.hasTemplate ? (
                        <>
                          <Badge 
                            variant={templateType.isEnabled ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {templateType.isEnabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedTemplateType(templateType.type);
                              setCurrentView('edit');
                            }}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge variant="outline" className="text-xs">
                            Not Created
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCreateNew(templateType.type)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Create
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render edit view
  const templateInfo = templateTypes.find(t => t.type === selectedTemplateType);
  
  if (currentTemplate === undefined && selectedTemplateType && templateTypesData) {
    return (
      <div className="flex items-center justify-center h-32" role="status" aria-live="polite" aria-busy="true">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCurrentView('list');
                setSelectedTemplateType(null);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Templates
            </Button>
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="h-5 w-5" />
                <span>{templateInfo?.name || 'Email Template'}</span>
                {isModified && (
                  <Badge variant="outline" className="ml-2">
                    Modified
                  </Badge>
                )}
                {isCreating && (
                  <Badge variant="outline" className="ml-2">
                    New Template
                  </Badge>
                )}
              </CardTitle>
              {templateInfo && (
                <p className="text-sm text-gray-600 mt-1">
                  {templateInfo.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {currentTemplate && !isCreating && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>
        {currentTemplate && !isCreating && (
          <p className="text-sm text-gray-600">
            Last updated: {formatDate(currentTemplate.updatedAt)}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Information section */}
        <details className="group bg-blue-50 border border-blue-200 rounded-lg">
          <summary className="p-4 cursor-pointer hover:bg-blue-100/50 transition-colors flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Info className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Template Variables</span>
            </div>
            <ChevronDown className="h-4 w-4 text-blue-600 transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <div className="px-4 pb-4 text-sm text-blue-800">
            <p className="mb-2">You can use the following variables in your template:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <p className="font-medium text-xs mb-1">Site Configuration:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><code>{'{{appName}}'}</code> - Application name</li>
                  <li><code>{'{{companyName}}'}</code> - Company name</li>
                  <li><code>{'{{companyEmail}}'}</code> - Company email</li>
                  <li><code>{'{{supportEmail}}'}</code> - Support email</li>
                  <li><code>{'{{websiteUrl}}'}</code> - Website URL</li>
                  <li><code>{'{{emailFooter}}'}</code> - Email footer text</li>
                </ul>
                <p className="font-medium text-xs mb-1 mt-2">Brand Colors:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><code>{'{{primaryColor}}'}</code> - Primary brand color</li>
                  <li><code>{'{{secondaryColor}}'}</code> - Secondary brand color</li>
                  <li><code>{'{{accentColor}}'}</code> - Accent/warning color</li>
                  <li><code>{'{{successColor}}'}</code> - Success color</li>
                  <li><code>{'{{errorColor}}'}</code> - Error color</li>
                  <li><code>{'{{backgroundColor}}'}</code> - Background color</li>
                  <li><code>{'{{textColor}}'}</code> - Primary text color</li>
                  <li><code>{'{{mutedTextColor}}'}</code> - Muted text color</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-xs mb-1">Context Specific:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><code>{'{{resetUrl}}'}</code> - The password reset link (password reset)</li>
                  <li><code>{'{{userEmail}}'}</code> - The user's email address</li>
                  <li><code>{'{{email}}'}</code> - Alias for userEmail (legacy)</li>
                  <li><code>{'{{token}}'}</code> - The token (for advanced use)</li>
                </ul>
              </div>
            </div>
          </div>
        </details>

        {/* Template settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Email Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => handleFieldChange('subject', e.target.value)}
              placeholder="Enter email subject..."
              maxLength={150}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="isEnabled">Status</Label>
            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="isEnabled"
                checked={isEnabled}
                onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isEnabled" className="text-sm">
                Template is enabled
              </Label>
            </div>
          </div>
        </div>

        {/* Description field */}
        <div className="space-y-2">
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Enter template description..."
            rows={2}
          />
        </div>

        {/* HTML content field */}
        <div className="space-y-2">
          <Label htmlFor="htmlContent">HTML Content</Label>
          <div 
            className="border rounded-md overflow-hidden"
            style={{
              // Set CSS variables for color previews
              '--primary-color': siteConfig?.primaryColor || '#000000',
              '--secondary-color': siteConfig?.secondaryColor || '#000000',
              '--accent-color': siteConfig?.accentColor || '#000000',
              '--success-color': siteConfig?.successColor || '#000000',
              '--error-color': siteConfig?.errorColor || '#000000',
              '--background-color': siteConfig?.backgroundColor || '#000000',
              '--text-color': siteConfig?.textColor || '#000000',
              '--muted-text-color': siteConfig?.mutedTextColor || '#000000',
            } as React.CSSProperties}
          >
            <CodeMirror
              value={htmlContent}
              onChange={(value) => handleFieldChange('htmlContent', value)}
              extensions={[
                html(),
                emailContentTheme,
                emailContentPlugin,
                EditorView.lineWrapping,
              ]}
              theme={undefined} // Use light theme by default, respects system preference
              placeholder="Enter HTML content..."
              className="text-sm"
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                dropCursor: false,
                allowMultipleSelections: false,
                indentOnInput: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                highlightSelectionMatches: false,
                searchKeymap: true,
                findAndReplace: true,
              }}
              style={{
                fontSize: '14px',
                minHeight: '300px',
              }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={!isModified || isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Template'}
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={!isModified || isSaving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Changes
          </Button>
          <Button 
            variant="secondary" 
            onClick={validateAndFormatEmail} 
            disabled={validationResults.isValidating}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {validationResults.isValidating ? 'Validating...' : 'Validate Email'}
          </Button>
        </div>

        {/* Validation Results */}
        {validationResults.warnings.length > 0 && (
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Email Validation Results
              </h4>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Score:</span>
                <Badge 
                  variant={validationResults.score >= 80 ? 'default' : validationResults.score >= 60 ? 'secondary' : 'destructive'}
                >
                  {validationResults.score}/100
                </Badge>
              </div>
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {validationResults.warnings.map((warning, index) => (
                <div 
                  key={index} 
                  className={`flex items-start space-x-2 p-2 rounded text-sm ${
                    warning.type === 'error' 
                      ? 'bg-red-50 text-red-800 border border-red-200' 
                      : warning.type === 'warning'
                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : 'bg-blue-50 text-blue-800 border border-blue-200'
                  }`}
                >
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    warning.type === 'error' 
                      ? 'text-red-600' 
                      : warning.type === 'warning'
                      ? 'text-amber-600'
                      : 'text-blue-600'
                  }`} />
                  <span className="flex-1">{warning.message}</span>
                </div>
              ))}
            </div>
            
            <div className="text-xs text-gray-600 border-t pt-2">
              <strong>Tip:</strong> Higher scores improve deliverability. Aim for 80+ for best results.
            </div>
          </div>
        )}

        {/* Preview section */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium mb-3">Preview</h4>
          {siteConfig && htmlContent.trim() ? (
          <div className="space-y-4">
            {/* Subject Preview */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Subject: </span>
                <span className="text-sm">
                  {replaceTemplateVariables(subject) || 'No subject'}
                </span>
              </div>
            </div>

            {/* Rendered HTML Preview */}
            <div className="border rounded-lg p-4 bg-white">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Rendered Email:</h5>
              <div className="border rounded bg-gray-50 overflow-x-hidden overflow-y-auto max-h-[800px]">
                <div className="flex justify-center py-8 px-4">
                  <div 
                    className="w-[600px] bg-white shadow-sm rounded"
                    dangerouslySetInnerHTML={{
                      __html: replaceTemplateVariables(htmlContent)
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Raw HTML Preview (collapsible) */}
            <details className="border rounded-lg p-4 bg-gray-50">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">View Raw HTML</summary>
              <div className="mt-2 border rounded p-3 bg-white max-h-32 overflow-y-auto">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {replaceTemplateVariables(htmlContent)}
                </pre>
              </div>
            </details>
          </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">
                  {!siteConfig ? 'Loading configuration...' : 'Loading template content...'}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}